/**
 * ERP-FIN-02 Gate AR-02: AR Receipt Detail
 * Route: /ar/receipts/:receiptId  (ar.receipt.view)
 *
 * Full receipt detail: verify, allocate to invoices, apply advances, reverse, audit trail.
 * AR-ATOMIC-01 allocation protocol — partial-success batch results shown inline.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, XCircle, Clock, Archive, AlertTriangle,
  Send, Plus, RefreshCw, DollarSign, TrendingDown, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arReceiptApi, arInvoiceApi, formatError } from "@/services/api";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 2 }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const STATUS_CFG = {
  PENDING_VERIFICATION: { label: "Pending Verification", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", Icon: Clock },
  VERIFIED:             { label: "Verified",             color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE", Icon: CheckCircle2 },
  PARTIALLY_ALLOCATED:  { label: "Partially Allocated",  color: "#166534", bg: "#F0FDF4", border: "#BBF7D0", Icon: TrendingDown },
  FULLY_ALLOCATED:      { label: "Fully Allocated",      color: "#166534", bg: "#F0FDF4", border: "#BBF7D0", Icon: CheckCircle2 },
  REVERSED:             { label: "Reversed",             color: "#6B7280", bg: "#F5F5F5", border: "#E5E7EB", Icon: Archive },
};

function StatusPill({ status }) {
  const c = STATUS_CFG[status] || { label: status, color: "#666", bg: "#F5F5F5", border: "#DDD", Icon: Clock };
  return (
    <span data-testid="receipt-status-pill" style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 700, background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      <c.Icon size={12} /> {c.label}
    </span>
  );
}

const TABS = [
  { key: "receipt",     label: "Receipt" },
  { key: "allocations", label: "Allocations" },
  { key: "audit",       label: "Audit Trail" },
];

export default function ARReceiptDetail() {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [receipt,    setReceipt]    = useState(null);
  const [allocations, setAllocs]   = useState([]);
  const [audit,      setAudit]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState("receipt");
  const [submitting, setSubmitting] = useState(false);

  // Modals
  const [showVerify,    setShowVerify]    = useState(false);
  const [showAllocate,  setShowAllocate]  = useState(false);
  const [showReverse,   setShowReverse]   = useState(false);

  // Verify fields
  const [verifyAmount,  setVerifyAmount]  = useState("");
  const [verifyNotes,   setVerifyNotes]   = useState("");
  const [verifyBankRef, setVerifyBankRef] = useState("");

  // Allocate: outstanding invoices for customer
  const [outstandingInvoices, setOutstandingInvs] = useState([]);
  const [allocationLines, setAllocLines] = useState([]);  // { invoice_id, amount }
  const [allocResult,    setAllocResult]  = useState(null);

  // Reverse
  const [reverseReason, setReverseReason] = useState("");
  const [reverseCode,   setReverseCode]   = useState("");
  const [reversalCodes, setReversalCodes] = useState([]);

  const hasCap    = (cap) => user?.capabilities?.includes(cap);
  const canVerify  = hasCap("ar.receipt.verify");
  const canRecord  = hasCap("ar.receipt.record");
  const canReverse = hasCap("ar.receipt.reverse");

  const isPending  = receipt?.status === "PENDING_VERIFICATION";
  const isVerified = receipt?.status === "VERIFIED";
  const isPartial  = receipt?.status === "PARTIALLY_ALLOCATED";
  const isFull     = receipt?.status === "FULLY_ALLOCATED";
  const isReversed = receipt?.status === "REVERSED";
  const canAllocate = (isVerified || isPartial) && canRecord;
  const canReverseReceipt = !isFull && !isReversed && canReverse;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await arReceiptApi.get(receiptId);
      setReceipt(res.data);
      setAllocs(res.data.allocations || []);
    } catch (err) {
      toast.error(formatError(err));
      if (err.response?.status === 404) navigate("/ar/receipts");
    } finally {
      setLoading(false);
    }
  }, [receiptId, navigate]);

  const loadAudit = useCallback(async () => {
    if (tab !== "audit") return;
    try {
      const res = await arReceiptApi.getAudit(receiptId);
      setAudit(Array.isArray(res.data) ? res.data : []);
    } catch { /* non-blocking */ }
  }, [receiptId, tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const loadOutstandingInvoices = useCallback(async () => {
    if (!showAllocate || !receipt?.customer_id) return;
    try {
      const res = await arReceiptApi.getCustomerOutstanding(receipt.customer_id);
      setOutstandingInvs(res.data.invoices || []);
      setAllocLines((res.data.invoices || []).map(inv => ({ invoice_id: inv.id, amount: "" })));
    } catch { /* non-blocking */ }
  }, [showAllocate, receipt?.customer_id]);

  useEffect(() => { loadOutstandingInvoices(); }, [loadOutstandingInvoices]);

  const loadReversalCodes = useCallback(async () => {
    if (!showReverse) return;
    try {
      const res = await arReceiptApi.getMDMConfig("REVERSAL_REASON_CODE");
      setReversalCodes(res.data?.items || []);
    } catch { /* non-blocking */ }
  }, [showReverse]);
  useEffect(() => { loadReversalCodes(); }, [loadReversalCodes]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const doVerify = async () => {
    if (!verifyAmount) { toast.error("Confirmed amount is required"); return; }
    setSubmitting(true);
    try {
      await arReceiptApi.verify(receiptId, {
        confirmed_amount_minor: Math.round(parseFloat(verifyAmount) * 100),
        verification_notes: verifyNotes,
        bank_settlement_ref: verifyBankRef,
      });
      toast.success("Receipt verified — ready for allocation");
      setShowVerify(false);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const doAllocateBatch = async () => {
    const lines = allocationLines
      .filter(l => l.amount && parseFloat(l.amount) > 0)
      .map(l => ({ invoice_id: l.invoice_id, allocation_amount_minor: Math.round(parseFloat(l.amount) * 100) }));

    if (lines.length === 0) { toast.error("Enter at least one allocation amount"); return; }
    setSubmitting(true);
    try {
      const res = await arReceiptApi.allocateBatch(receiptId, { lines });
      setAllocResult(res.data);
      const { committed, failed } = res.data;
      if (failed === 0) toast.success(`All ${committed} allocation(s) committed`);
      else toast.warning(`Partial success: ${committed} committed, ${failed} failed`);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const doReverseAlloc = async (aid) => {
    if (!window.confirm("Reverse this allocation? This will restore the unapplied balance.")) return;
    try {
      await arReceiptApi.reverseAllocation(aid, { reversal_reason: "Manual reversal requested by user" });
      toast.success("Allocation reversed");
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const doReverse = async () => {
    if (!reverseReason.trim()) { toast.error("Reversal reason is required"); return; }
    setSubmitting(true);
    try {
      await arReceiptApi.reverse(receiptId, { reversal_reason: reverseReason, reversal_reason_code: reverseCode || null });
      toast.success("Receipt reversed");
      setShowReverse(false);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ padding: "48px", textAlign: "center", color: "#6B7280" }}>Loading receipt…</div>;
  if (!receipt) return null;

  const currency = receipt.currency_code || "INR";
  const unapplied = receipt.unapplied_amount_minor || 0;
  const applied   = receipt.applied_amount_minor   || 0;
  const confirmed = receipt.confirmed_amount_minor || 0;

  return (
    <div data-testid="ar-receipt-detail" style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button onClick={() => navigate("/ar/receipts")} style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: "13px" }}>
          <ArrowLeft size={14} /> Back
        </button>
        <h1 data-testid="receipt-number-heading" style={{ fontSize: "20px", fontWeight: 700, color: "#111827", margin: 0, flex: 1 }}>
          {receipt.receipt_number}
        </h1>
        <StatusPill status={receipt.status} />

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {isPending && canVerify && (
            <button data-testid="verify-btn" onClick={() => setShowVerify(true)}
              style={{ padding: "7px 14px", background: "#1D4ED8", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
              Verify Receipt
            </button>
          )}
          {canAllocate && (
            <button data-testid="allocate-btn" onClick={() => setShowAllocate(true)}
              style={{ padding: "7px 14px", background: "#166534", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
              Allocate
            </button>
          )}
          {canReverseReceipt && (
            <button data-testid="reverse-receipt-btn" onClick={() => setShowReverse(true)}
              style={{ padding: "7px 14px", background: "#fff", color: "#DC2626", border: "1px solid #FECACA", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
              Reverse
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #E5E7EB", marginBottom: "20px" }}>
        {TABS.map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            style={{ padding: "8px 16px", background: "none", border: "none", borderBottom: tab === t.key ? "2px solid #0176D3" : "2px solid transparent", color: tab === t.key ? "#0176D3" : "#6B7280", fontWeight: tab === t.key ? 600 : 400, fontSize: "13px", cursor: "pointer", marginBottom: "-1px" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Receipt ── */}
      {tab === "receipt" && (
        <div>
          {/* Balance Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "12px", marginBottom: "20px" }}>
            {[
              { label: "Received Amount", value: receipt.receipt_amount_minor, color: "#111827" },
              { label: "Confirmed Amount", value: confirmed, color: "#1D4ED8" },
              { label: "Applied Amount",   value: applied,   color: "#166534" },
              { label: "Unapplied Balance", value: unapplied, color: unapplied > 0 ? "#92400E" : "#166534" },
            ].map(({ label, value, color }) => (
              <div key={label} data-testid={`balance-${label.toLowerCase().replace(/ /g,"-")}`}
                style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "14px" }}>
                <div style={{ fontSize: "11px", color: "#6B7280", marginBottom: "4px" }}>{label}</div>
                <div style={{ fontSize: "18px", fontWeight: 700, color }}>{fmt(value, currency)}</div>
              </div>
            ))}
          </div>

          {/* Receipt Info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", marginBottom: "10px" }}>Receipt Details</div>
              {[
                { label: "Customer",          value: receipt.customer_name },
                { label: "Legal Entity",      value: receipt.legal_entity_name },
                { label: "Event",             value: receipt.event_name },
                { label: "Receipt Date",      value: fmtDate(receipt.receipt_date) },
                { label: "Accounting Date",   value: fmtDate(receipt.accounting_date) },
                { label: "Method",            value: receipt.receipt_method },
                { label: "Currency",          value: currency },
              ].map(({ label, value }) => value && (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "13px", borderBottom: "1px solid #F3F4F6" }}>
                  <span style={{ color: "#6B7280" }}>{label}</span>
                  <span style={{ fontWeight: 500, color: "#111827" }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", marginBottom: "10px" }}>Bank References</div>
              {[
                { label: "External Reference", value: receipt.external_reference },
                { label: "Bank Account Ref",   value: receipt.bank_account_ref },
                { label: "Settlement Ref",     value: receipt.bank_settlement_ref },
                { label: "Narration",          value: receipt.narration },
              ].map(({ label, value }) => value && (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "13px", borderBottom: "1px solid #F3F4F6" }}>
                  <span style={{ color: "#6B7280" }}>{label}</span>
                  <span style={{ fontWeight: 500, color: "#111827", wordBreak: "break-all" }}>{value}</span>
                </div>
              ))}
              {receipt.verified_by_email && (
                <div style={{ marginTop: "8px", fontSize: "12px", color: "#374151" }}>
                  Verified by <strong>{receipt.verified_by_email}</strong> on {fmtDT(receipt.verified_at)}
                </div>
              )}
              {isReversed && (
                <div style={{ marginTop: "8px", fontSize: "12px", color: "#DC2626" }}>
                  <strong>REVERSED</strong> by {receipt.reversed_by_email} — {receipt.reversal_reason}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Allocations ── */}
      {tab === "allocations" && (
        <div data-testid="allocations-tab">
          {allocations.length === 0 ? (
            <div style={{ padding: "32px", textAlign: "center", color: "#9CA3AF", fontSize: "13px" }}>
              No allocations yet.{canAllocate ? " Use the Allocate button to assign this receipt against outstanding invoices." : ""}
            </div>
          ) : (
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F9FAFB" }}>
                    {["Date", "Invoice", "Amount", "Type", "Status", canReverse ? "" : null].filter(Boolean).map(h => (
                      <th key={h} style={{ padding: "9px 14px", textAlign: h === "Amount" ? "right" : "left", fontSize: "11px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((al, idx) => (
                    <tr key={al.id} data-testid={`alloc-row-${al.id}`} style={{ borderBottom: idx < allocations.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                      <td style={{ padding: "10px 14px", fontSize: "13px", color: "#374151" }}>{fmtDate(al.allocation_date)}</td>
                      <td style={{ padding: "10px 14px", fontSize: "13px", color: "#0176D3", fontWeight: 500 }}>{al.invoice_id?.slice(0, 8)}…</td>
                      <td style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 600, color: "#111827", textAlign: "right" }}>{fmt(al.allocation_amount_minor, currency)}</td>
                      <td style={{ padding: "10px 14px", fontSize: "12px", color: "#6B7280" }}>{al.is_advance_application ? "Advance" : "Receipt"}</td>
                      <td style={{ padding: "10px 14px" }}>
                        {al.is_reversed
                          ? <span style={{ fontSize: "11px", padding: "2px 8px", background: "#F5F5F5", color: "#6B7280", borderRadius: "12px", fontWeight: 600 }}>REVERSED</span>
                          : <span style={{ fontSize: "11px", padding: "2px 8px", background: "#F0FDF4", color: "#166534", borderRadius: "12px", fontWeight: 600 }}>ACTIVE</span>}
                      </td>
                      {canReverse && (
                        <td style={{ padding: "10px 14px" }}>
                          {!al.is_reversed && (
                            <button data-testid={`reverse-alloc-${al.id}`} onClick={() => doReverseAlloc(al.id)}
                              style={{ background: "none", border: "1px solid #FECACA", color: "#DC2626", padding: "3px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}>
                              Reverse
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Audit ── */}
      {tab === "audit" && (
        <div data-testid="audit-trail-tab">
          {audit.length === 0
            ? <div style={{ padding: "32px", textAlign: "center", color: "#9CA3AF" }}>No audit events.</div>
            : audit.map((ev, i) => (
              <div key={i} data-testid={`audit-event-${i}`} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "12px 16px", marginBottom: "8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "#0176D3", background: "#EFF6FF", padding: "2px 8px", borderRadius: "12px" }}>{ev.event_type || ev.action}</span>
                  <span style={{ fontSize: "11px", color: "#9CA3AF" }}>{fmtDT(ev.timestamp || ev.created_at)}</span>
                </div>
                <div style={{ fontSize: "13px", color: "#374151", marginTop: "6px" }}>{ev.summary || "—"}</div>
                {ev.actor_email && <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "3px" }}>By: {ev.actor_email}</div>}
              </div>
            ))
          }
        </div>
      )}

      {/* ── Modals ── */}

      {/* Verify Modal */}
      {showVerify && (
        <Modal title="Verify Receipt" onClose={() => setShowVerify(false)}>
          <p style={{ fontSize: "13px", color: "#374151", marginBottom: "12px" }}>Confirm the actual bank-received amount for <strong>{receipt.receipt_number}</strong>. This may differ from the recorded amount (e.g., bank rounding).</p>
          <label style={lbl}>Confirmed Amount (₹) *</label>
          <input data-testid="verify-amount-input" type="number" min="0.01" step="0.01" required
            value={verifyAmount} onChange={e => setVerifyAmount(e.target.value)}
            placeholder={`Expected: ${receipt.receipt_amount_minor / 100}`}
            style={{ ...inp, width: "100%", boxSizing: "border-box", marginBottom: "12px" }} />
          <label style={lbl}>Bank Settlement Reference</label>
          <input data-testid="verify-bank-ref" type="text" value={verifyBankRef} onChange={e => setVerifyBankRef(e.target.value)}
            placeholder="Bank-provided confirmation / UTR" style={{ ...inp, width: "100%", boxSizing: "border-box", marginBottom: "12px" }} />
          <label style={lbl}>Verification Notes</label>
          <textarea data-testid="verify-notes" value={verifyNotes} onChange={e => setVerifyNotes(e.target.value)}
            rows={2} placeholder="Optional notes…" style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} />
          <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "6px", padding: "10px 12px", marginTop: "12px", fontSize: "12px", color: "#92400E" }}>
            Note: Receipts above ₹5,00,000 must be verified by a different user than the recorder (financial SoD).
          </div>
          <ModalButtons onCancel={() => setShowVerify(false)} onConfirm={doVerify}
            confirmLabel="Verify Receipt" confirmColor="#1D4ED8" loading={submitting} testId="confirm-verify-btn" />
        </Modal>
      )}

      {/* Allocate Modal */}
      {showAllocate && (
        <Modal title="Allocate Receipt" onClose={() => { setShowAllocate(false); setAllocResult(null); }} wide>
          {allocResult ? (
            <div>
              <div style={{ marginBottom: "12px", fontSize: "13px", fontWeight: 600, color: allocResult.failed === 0 ? "#166534" : "#92400E" }}>
                {allocResult.failed === 0 ? "All allocations committed successfully." : `Partial success: ${allocResult.committed} committed, ${allocResult.failed} failed.`}
              </div>
              {allocResult.results?.map((r, i) => (
                <div key={i} style={{ padding: "8px 12px", borderRadius: "6px", marginBottom: "6px", background: r.status === "COMMITTED" || r.status === "ALREADY_COMMITTED" ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${r.status === "COMMITTED" || r.status === "ALREADY_COMMITTED" ? "#BBF7D0" : "#FECACA"}`, fontSize: "13px" }}>
                  <span style={{ fontWeight: 600, color: r.status === "COMMITTED" || r.status === "ALREADY_COMMITTED" ? "#166534" : "#DC2626" }}>{r.status}</span>
                  {" — "}{fmt(r.amount, currency)}
                  {r.reason && <span style={{ color: "#DC2626" }}> — {r.reason}</span>}
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
                <button onClick={() => { setShowAllocate(false); setAllocResult(null); }} style={{ padding: "7px 16px", background: "#0176D3", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Done</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "6px", padding: "10px 12px", marginBottom: "14px", fontSize: "12px", color: "#1D4ED8" }}>
                Unapplied Balance: <strong>{fmt(unapplied, currency)}</strong> · Each line commits independently (AR-ATOMIC-01 partial-success semantics).
              </div>
              {outstandingInvoices.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#6B7280" }}>No outstanding invoices found for this customer.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "12px" }}>
                  <thead>
                    <tr style={{ background: "#F9FAFB" }}>
                      {["Invoice #", "Due Date", "Outstanding", "Allocate (₹)"].map(h => (
                        <th key={h} style={{ padding: "8px 10px", textAlign: h === "Outstanding" || h === "Allocate (₹)" ? "right" : "left", fontSize: "11px", fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #E5E7EB" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {outstandingInvoices.map((inv, idx) => (
                      <tr key={inv.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                        <td style={{ padding: "8px 10px", fontSize: "13px", fontWeight: 500, color: "#0176D3" }}>{inv.invoice_number}</td>
                        <td style={{ padding: "8px 10px", fontSize: "12px", color: "#6B7280" }}>{fmtDate(inv.due_date)}</td>
                        <td style={{ padding: "8px 10px", fontSize: "13px", fontWeight: 600, textAlign: "right" }}>{fmt(inv.outstanding_minor, currency)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>
                          <input type="number" min="0" step="0.01"
                            data-testid={`alloc-amount-${inv.id}`}
                            value={allocationLines.find(l => l.invoice_id === inv.id)?.amount || ""}
                            onChange={e => setAllocLines(lines => lines.map(l => l.invoice_id === inv.id ? { ...l, amount: e.target.value } : l))}
                            placeholder="0.00"
                            style={{ padding: "5px 8px", border: "1px solid #D1D5DB", borderRadius: "4px", fontSize: "12px", width: "100px", textAlign: "right" }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <ModalButtons onCancel={() => setShowAllocate(false)} onConfirm={doAllocateBatch}
                confirmLabel="Commit Allocation" confirmColor="#166534" loading={submitting} testId="confirm-allocate-btn" />
            </>
          )}
        </Modal>
      )}

      {/* Reverse Receipt Modal */}
      {showReverse && (
        <Modal title="Reverse Receipt" onClose={() => setShowReverse(false)}>
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "6px", padding: "10px 12px", marginBottom: "12px", fontSize: "12px", color: "#991B1B" }}>
            This will permanently reverse the receipt. Ensure all allocations are reversed first.
          </div>
          <label style={lbl}>Reversal Reason Code</label>
          <select data-testid="reversal-code-select" value={reverseCode} onChange={e => setReverseCode(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box", marginBottom: "12px" }}>
            <option value="">— Optional —</option>
            {reversalCodes.map(rc => <option key={rc.item_code} value={rc.item_code}>{rc.display_name}</option>)}
          </select>
          <label style={lbl}>Reversal Reason *</label>
          <textarea data-testid="reversal-reason-input" required value={reverseReason} onChange={e => setReverseReason(e.target.value)}
            rows={3} minLength={10} placeholder="Mandatory reason (min 10 characters)…"
            style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} />
          <ModalButtons onCancel={() => setShowReverse(false)} onConfirm={doReverse}
            confirmLabel="Reverse Receipt" confirmColor="#DC2626" loading={submitting} testId="confirm-reverse-btn" />
        </Modal>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
const lbl = { display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
const inp = { padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px" };

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: wide ? "700px" : "480px", maxHeight: "90vh", overflowY: "auto" }}>
        <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#111827", marginBottom: "16px", marginTop: 0 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}
function ModalButtons({ onCancel, onConfirm, confirmLabel, confirmColor, loading, testId }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
      <button onClick={onCancel} style={{ padding: "7px 16px", border: "1px solid #D1D5DB", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
      <button data-testid={testId} onClick={onConfirm} disabled={loading}
        style={{ padding: "7px 16px", background: loading ? "#9CA3AF" : confirmColor, color: "#fff", border: "none", borderRadius: "6px", cursor: loading ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
        {loading ? "Processing…" : confirmLabel}
      </button>
    </div>
  );
}
